import { Link } from "react-router-dom"
import { translations } from "../translations"
import { app, translate } from "../app"
import { formatServiceDistance } from "../helpers"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { GetIconForChannel, getContactDetailLink, getCategoryIcon } from "./service"
import { languages } from "../locale"
import { useAnimateOnScroll } from "./useAnimateOnScroll"
import { LiveHelp, PinDrop, RightOutlined } from "./icons"
import { Pagination } from "./pagination"

import { Select } from "./select"
import { trackServiceDetailsClick, trackZeroResults } from "../map-analytics"

interface ServiceListProps {
  services: Service[]
  serviceCount: number
  userLocation?: { latitude: number; longitude: number } | null
}

const html2text = document.createElement("div")
html2text.hidden = true

export function ServicesList({ services, serviceCount, userLocation = null }: ServiceListProps) {

  useAnimateOnScroll()
  const listTopRef = useRef<HTMLDivElement>(null)
  const [currentPage, setCurrentPage] = useState(1)

  const orderedServices = services.sort((a, b) => {
    const labelA = translate(a.name).toUpperCase()
    const labelB = translate(b.name).toUpperCase()
    if (labelA < labelB) return -1
    if (labelA > labelB) return 1
    return 0
  })

  const [filteredServices, setFilteredServices] = useState<Service[]>(orderedServices)
  const [servicesList, setServicesList] = useState<Service[]>(orderedServices.slice(0, 10))

  const mapAutocompleteOptions = useMemo(() => {
    const linkedCities = new Set(services.map((service) => service.city))
    const linkedRegions = new Set(services.map((service) => service.region))

    const regionNames = Array.from(linkedRegions).map((region) => {
      return {
        label: translate(region),
        value: `R${translate(region)}`,
      }
    })

    const cityNames = Array.from(linkedCities).map((city) => {
      return {
        label: translate(city),
        value: `C${translate(city)}`,
      }
    })

    const allOptions = [...regionNames, ...cityNames]

    return allOptions.sort((a, b) => {
      const labelA = a.label.toUpperCase().trim()
      const labelB = b.label.toUpperCase().trim()
      if (labelA < labelB) return -1
      if (labelA > labelB) return 1
      return 0
    })
  }, [services])

  const onSelect = useCallback((data: { key: string; label: string; value: string }) => {
    let matches: Service[]
    if (data.value.charAt(0) === "S") {
      matches = services.filter((x) => x.id === +data.value.substring(1))
    } else if (data.value.charAt(0) === "R") {
      matches = services.filter((x) => x.region === data.value.substring(1))
    } else {
      matches = services.filter((x) => x.city === data.value.substring(1))
    }
    setFilteredServices(matches)
    if (matches.length === 0) {
      trackZeroResults({ searchType: "list_search", searchTerm: data.label, view: "list" })
    }
  }, [services])

  const handlePageChange = (page: number, pageSize: number) => {
    setServicesList(filteredServices.slice((page - 1) * pageSize, Math.min(services.length, page * pageSize)))
    setCurrentPage(page)
  }

  const didMountRef = useRef(false)
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      return
    }
    listTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }, [currentPage])

  useEffect(() => {
    setServicesList(filteredServices.slice(0, 10))
  }, [filteredServices])

  useEffect(() => {
    setFilteredServices(services)
  }, [services])


  return <div ref={listTopRef}>
    {app.country !== 42 && app.country !== 43 && <div className="w-full top-3.5 flex md:items-center flex-col md:flex-row gap-4 fade-up-0" data-animation="animate__fadeIn">
      <span className="text-gray-600 text-sm">{translate(translations.showing)} {filteredServices.length} {translate(translations.of)} {serviceCount} </span>
      <Select
        className="md:w-4/12 lg:w-6/12 fade-up-1"
        options={mapAutocompleteOptions}
        placeholder={translate(translations.search)}
        listHeight={100}
        onSelect={onSelect}
        allowClear
        onClear={() => { setFilteredServices(services) }}
        filterOption={(inputValue, option) => option && option.label ? option!.label.toString().toUpperCase().indexOf(inputValue.toUpperCase()) !== -1 : false}
        data-animation="animate__fadeIn"
      />
    </div>}
    <div className={`grid-cols-2 gap-4 mt-4 mb-4 flex flex-col fade-up-2 ${app.country !== 42 && app.country !== 43 ? "md:grid" : ""}`} data-animation="animate__fadeIn">
      {servicesList.map((s) => (
        <Service key={s.id} service={s} userLocation={userLocation} />
      ))}
    </div>
    <Pagination className="mt-8" onChange={handlePageChange} total={filteredServices.length} pageSize={10} current={currentPage} />
  </div>

}

function Service(props: {
  service: Service
  userLocation?: { latitude: number; longitude: number } | null
}) {

  const { service: s, userLocation = null } = props
  const locale = languages[app.locale]?.zendesk as string ?? app.locale
  const isSwitchboard = app.country === 42
  const isOrPrograms = app.country === 43
  const useMiles = isSwitchboard || isOrPrograms
  const distanceLabel =
    userLocation != null
      ? formatServiceDistance(userLocation, s.location, useMiles)
      : null
  const firstCategoryId = s.categories?.[0]
  const firstCategory =
    firstCategoryId != null
      ? app.data.categories.categories[firstCategoryId]
      : undefined
  const CategoryIcon = getCategoryIcon(firstCategory?.icon) || LiveHelp
  const categoryAccent = firstCategory?.color || "#2563a8"

  const displayName = isSwitchboard || isOrPrograms
    ? (s.name?.["en-US"] ?? "")
    : translate(s.name)
  const displayAddress = s.address ?? ""

  html2text.innerHTML = translate(s.description)

  const description = `${html2text.textContent.substring(0, 200)}...`

  return (
    <Link key={s.id} to={`/${locale.toLowerCase()}/services/${s.id}`} onClick={() => trackServiceDetailsClick({ service: s, source: "list_card" })} className="service-card no-underline flex flex-col h-full border-2 border-solid border-[#111827] bg-white transition-all hover:bg-[#f9fafb] rounded-[15px]">
      <div className="flex flex-row gap-4 h-full min-h-0 p-5">
        {(isSwitchboard || isOrPrograms) && firstCategoryId != null && (
          <span
            className="category-icon-chip"
            style={{
              backgroundColor: `${categoryAccent}3a`,
              color: categoryAccent,
            }}
          >
            <CategoryIcon />
          </span>
        )}
        <div className="flex flex-col text-black h-full min-w-0 flex-1">
        <div className="">
          <h2
            className={`service-card-title font-bold font-inter text-xl leading-7 mb-1 pb-2 border-b-2 border-[#111827] uppercase tracking-wide ${isSwitchboard || isOrPrograms ? "text-left" : ""}`}
            dir={isSwitchboard || isOrPrograms ? "ltr" : undefined}
            style={isSwitchboard || isOrPrograms ? { direction: "ltr", unicodeBidi: "isolate" } : undefined}
          >
            {displayName}
          </h2>
          {displayAddress && (
            <div
              className={`text-sm mb-3 flex items-center text-gray-700 ${isSwitchboard || isOrPrograms ? "text-left" : ""}`}
              dir={isSwitchboard || isOrPrograms ? "ltr" : undefined}
              style={isSwitchboard || isOrPrograms ? { direction: "ltr", unicodeBidi: "isolate" } : undefined}
            >
              <PinDrop className="text-lg mr-2" />
              <span>{displayAddress}</span>
            </div>
          )}
          {distanceLabel && (
            <div className="text-sm mb-3 text-gray-600">
              {distanceLabel} {translate(translations.away)}
            </div>
          )}
        </div>
        <div className="text-sm leading-6 text-gray-700 mb-4 flex-grow">{description}</div>
        {s?.contactInfo && s.contactInfo.length > 0 && (
          <div className="grid grid-cols-2 gap-3 mb-4">
            {s.contactInfo.map(info => {
              if (!info.contact_details) return null
              const icon = <GetIconForChannel channel={info.channel} />
              const contactDetail = getContactDetailLink({ channel: info.channel, contactDetails: info.contact_details, })
              return <div className="truncate flex items-center gap-2" key={`${info.contact_details}-${info.channel}`}>
                <div className="text-sm flex-shrink-0">{icon}</div>
                <div className="truncate text-[#111827] text-sm hover:underline">{contactDetail}</div>
              </div>
            })}
          </div>
        )}
        <div className="flex justify-end items-center text-sm text-[#111827] font-medium mt-auto pt-3 border-t border-gray-200">
          <span>{translate(translations.seeMoreDetails)}</span>
          <RightOutlined className="ml-1" />
        </div>
        </div>
      </div>
    </Link>
  )
}
